import type { LayerVersions } from '../api/types'
import type { LayerInfo } from './capabilities'

/**
 * Tile freshness (tile-cache roadmap, Sprint 2). The proxy empties GeoWebCache
 * when a source table changes, but browsers keep tiles for days and an open map
 * never redraws. Each layer therefore carries a version in its WMS parameters
 * (`V=`): a new version is a new tile URL, so the stale tiles are simply never
 * asked for again.
 *
 * The viewer invents nothing: versions are whatever `/api/layer-versions` says,
 * compared only for (in)equality and never parsed. A layer with no known version
 * is left exactly as it is.
 */

/** The slice of a TileLayer<TileWMS> this module touches — keeps it testable without OpenLayers. */
export interface VersionedLayer {
  get(key: string): unknown
  getSource(): {
    getParams(): Record<string, unknown>
    updateParams(params: Record<string, unknown>): void
  } | null
}

export const VERSION_PARAM = 'V'
export const REFRESH_INTERVAL_MS = 30 * 60 * 1000
/** A tab switch inside this window reuses the last answer instead of asking again. */
export const MIN_FETCH_GAP_MS = 60 * 1000

/**
 * The version of one advertised layer. The proxy keys are workspace-qualified
 * (`mining:canadian_claims`, the GWC layer name) while the real workspace
 * GetCapabilities advertises the bare name (`canadian_claims`), the mock a mix
 * of both — so `LayerInfo.name` is tried as is, then qualified (same two forms
 * as `matchDeepLinkLayers`). Own keys only: `constructor` is not a layer.
 */
export function versionOf(
  info: Pick<LayerInfo, 'workspace' | 'name'>,
  versions: LayerVersions,
): string | undefined {
  const own = (key: string): string | undefined =>
    Object.prototype.hasOwnProperty.call(versions, key) ? versions[key] : undefined
  return own(info.name) ?? own(`${info.workspace}:${info.name}`)
}

/**
 * Push each layer's version into its source. `updateParams` reloads every tile
 * of that source, so it runs only when the version really differs from the one
 * already in the params — an unchanged poll costs nothing on screen.
 * Returns how many layers were updated.
 */
export function applyVersions(layers: readonly VersionedLayer[], versions: LayerVersions): number {
  let updated = 0
  for (const layer of layers) {
    const info = layer.get('info') as LayerInfo | undefined
    if (!info) continue
    const version = versionOf(info, versions)
    if (!version) continue
    const source = layer.getSource()
    if (!source || source.getParams()[VERSION_PARAM] === version) continue
    source.updateParams({ [VERSION_PARAM]: version })
    updated += 1
  }
  return updated
}

export interface VersionRefreshOptions {
  fetcher: () => Promise<LayerVersions | null>
  /** Epoch ms of a fetch that already happened at boot, so startup does not repeat it. */
  fetchedAt?: number
  now?: () => number
  intervalMs?: number
  minGapMs?: number
}

/**
 * Keep the layers' versions current: every 30 min and whenever the tab becomes
 * visible again (skipped when the last fetch is under a minute old). The first
 * fetch runs immediately unless `fetchedAt` says boot already did it.
 * Returns the teardown (timer + listener).
 */
export function startVersionRefresh(
  layers: readonly VersionedLayer[],
  options: VersionRefreshOptions,
): () => void {
  const now = options.now ?? Date.now
  const minGap = options.minGapMs ?? MIN_FETCH_GAP_MS
  let lastFetch = options.fetchedAt ?? Number.NEGATIVE_INFINITY
  let inFlight = false
  let stopped = false

  const refresh = async (): Promise<void> => {
    if (inFlight || stopped) return
    inFlight = true
    lastFetch = now()
    try {
      const versions = await options.fetcher()
      if (versions && !stopped) applyVersions(layers, versions)
    } catch {
      // The fetcher contract is "never throws"; this guards against a violation.
    } finally {
      inFlight = false
    }
  }

  const onVisible = (): void => {
    if (document.visibilityState !== 'visible') return
    if (now() - lastFetch < minGap) return
    void refresh()
  }

  const timer = setInterval(() => void refresh(), options.intervalMs ?? REFRESH_INTERVAL_MS)
  document.addEventListener('visibilitychange', onVisible)
  if (options.fetchedAt === undefined) void refresh()

  return () => {
    stopped = true
    clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
